import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { X, MapPin, Globe, Flag, Loader2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

export interface DestinationOption {
  id: string;
  displayName: string;
  destinationType: "Region" | "Country" | "US State";
  parentRegion?: string | null;
}

interface DestinationSelectorProps {
  value: DestinationOption[];
  onChange: (destinations: DestinationOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  Region: <Globe className="h-3.5 w-3.5 text-blue-500 shrink-0" />,
  Country: <Flag className="h-3.5 w-3.5 text-green-600 shrink-0" />,
  "US State": <MapPin className="h-3.5 w-3.5 text-orange-500 shrink-0" />,
};

const TYPE_BADGE_COLORS: Record<string, string> = {
  Region: "bg-blue-50 text-blue-700 border-blue-200",
  Country: "bg-green-50 text-green-700 border-green-200",
  "US State": "bg-orange-50 text-orange-700 border-orange-200",
};

export default function DestinationSelector({
  value,
  onChange,
  placeholder = "Type to search destinations...",
  disabled = false,
  className,
}: DestinationSelectorProps) {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<DestinationOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const instanceId = useId();
  const listboxId = `${instanceId}-listbox`;
  const getOptionId = (index: number) => `${instanceId}-option-${index}`;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const selectedIds = new Set(value.map((d) => d.id));

  const fetchSuggestions = useCallback(async (query: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ q: query, limit: "30" });
      const res = await fetch(`/api/destinations/search?${params}`, {
        credentials: "include",
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(
          res.status === 503
            ? "Destination service is temporarily unavailable. Please try again."
            : `Failed to load destinations (${res.status})`
        );
      }
      const data: DestinationOption[] = await res.json();
      if (controller.signal.aborted) return;
      setSuggestions(data);
      setHighlightedIndex(-1);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load destinations. Please try again."
      );
      setSuggestions([]);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (inputValue.trim().length === 0) {
      abortRef.current?.abort();
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(inputValue.trim());
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, fetchSuggestions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addDestination = (dest: DestinationOption) => {
    if (selectedIds.has(dest.id)) return;
    onChange([...value, dest]);
    setInputValue("");
    setSuggestions([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const removeDestination = (id: string) => {
    onChange(value.filter((d) => d.id !== id));
  };

  const filteredSuggestions = suggestions.filter(
    (s) => !selectedIds.has(s.id)
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (!isOpen || filteredSuggestions.length === 0) {
      if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
        removeDestination(value[value.length - 1].id);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
        addDestination(filteredSuggestions[highlightedIndex]);
      }
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Selected destination chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((dest) => (
            <Badge
              key={dest.id}
              variant="secondary"
              className={cn(
                "py-1 px-2.5 text-sm font-normal border gap-1.5",
                TYPE_BADGE_COLORS[dest.destinationType] || ""
              )}
            >
              {TYPE_ICONS[dest.destinationType]}
              {dest.displayName}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${dest.displayName}`}
                  onClick={() => removeDestination(dest.id)}
                  className="inline-flex items-center justify-center rounded-sm ml-0.5 opacity-60 hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-activedescendant={isOpen && highlightedIndex >= 0 ? getOptionId(highlightedIndex) : undefined}
          aria-autocomplete="list"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => {
            if (inputValue.trim().length > 0 && suggestions.length > 0) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="pr-8"
        />
        {isLoading && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg overflow-hidden"
        >
          {error ? (
            <div className="flex items-center gap-2 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading destinations...
            </div>
          ) : filteredSuggestions.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {inputValue.trim().length > 0
                ? "No destinations found"
                : "Start typing to search"}
            </div>
          ) : (
            <ScrollArea className="max-h-[220px]">
              <ul className="py-1" role="listbox" id={listboxId}>
                {filteredSuggestions.map((dest, index) => (
                  <li
                    key={dest.id}
                    id={getOptionId(index)}
                    role="option"
                    aria-selected={index === highlightedIndex}
                    className={cn(
                      "flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm transition-colors",
                      index === highlightedIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50"
                    )}
                    onClick={() => addDestination(dest)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {TYPE_ICONS[dest.destinationType]}
                    <span className="flex-1">{dest.displayName}</span>
                    <span className="text-xs text-muted-foreground">
                      {dest.destinationType}
                    </span>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}
